import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App, { CMS_CITY_SERVICE_LOADERS } from './App';
import ChunkReloadBoundary from './components/ChunkReloadBoundary';
import { CMS_PAGES } from './data/cityServiceSlugMap';
import { loadEntriesForPath } from './data/cityServicePages/lazy';
import { i18nReady } from "./i18n";
import { captureAttribution } from './utils/attribution';
import { installPhoneTracking } from './utils/phoneTracking';

// Client-only helpers that never touch the rendered markup (hydration-safe):
// keep the landing URL's campaign params for the quote forms, and count phone taps.
captureAttribution();
installPhoneTracking();

const container = document.getElementById('root');

const app = (
  <React.StrictMode>
    <ChunkReloadBoundary>
      <App />
    </ChunkReloadBoundary>
  </React.StrictMode>
);

/**
 * Pre-fetch the lazy route chunk(s) for the current URL so React's Suspense
 * fallback never replaces the prerendered HTML with a blank 100vh div
 * during hydration. Without this, every prerendered page suffers a massive
 * CLS hit (~0.5+) because content disappears momentarily while the route
 * chunk downloads.
 */
const preloadCurrentRoute = () => {
  const raw = (window.location.pathname || '/').replace(/^\/fr(?=\/|$)/, '') || '/';
  // strip trailing slash for matching
  const p = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;

  // Home page renders Hero (eager) plus 7 lazy sub-sections. We must preload
  // them all or Suspense will blank the entire page (CLS = 1.0).
  if (p === '/' || p === '') {
    return Promise.all([
      import('./Sections/About'),
      import('./Sections/Cities'),
      import('./Sections/WhyChooseUs'),
      import('./Sections/Services'),
      import('./Sections/Team'),
      import('./Sections/Testimonial'),
      import('./Sections/Blog'),
    ]);
  }
  if (p === '/about') return import('./components/AboutPage');
  if (p === '/contact') return import('./Sections/Contact');
  if (p === '/careers') return import('./Sections/Careers/Careers.jsx');
  if (p === '/security-careers') return import('./components/CareersInfoPage');
  if (p === '/blog') return import('./components/BlogListPage');
  if (p === '/blog/foundation') return import('./Sections/Foundation');
  if (p.startsWith('/blog/')) return import('./components/BlogPostRoute');
  // French case studies live under /fr/etudes-de-cas
  if (p === '/case-studies' || p === '/etudes-de-cas') return import('./components/CaseStudiesIndex');
  if (p.startsWith('/case-studies/') || p.startsWith('/etudes-de-cas/')) return import('./components/CaseStudyPage');
  if (p.startsWith('/industries/')) return import('./components/IndustryPage');
  if (p.startsWith('/compliance')) return import('./components/CompliancePage');
  if (p === '/privacy') return import('./components/PrivacyPolicy');
  if (p === '/terms') return import('./components/TermsOfService');
  if (p === '/commercial-properties') return import('./components/CommercialPropertiesPage');
  if (p === '/mobile-patrol' || p === '/patrol') return import('./components/PatrolPage');
  if (p.startsWith('/services/')) {
    return Promise.all([
      import('./components/ServiceRoute'),
      import('./components/ServicePage'),
      import('./components/ServiceCategoryPage'),
      import('./components/ServiceTemplate/ServiceTemplate'),
    ]);
  }
  // City pages and city-service pages
  if (p === '/toronto') return import('./components/CityPages/TorontoPage');
  if (p === '/montreal') return import('./components/CityPages/MontrealPage');
  if (p === '/ottawa') return import('./components/CityPages/OttawaPage');
  if (p === '/quebec-city' || p === '/quebec') return import('./components/CityPages/QuebecCityPage');
  if (p === '/trois-rivieres') return import('./components/CityPages/TroisRivieresPage');
  if (p === '/niagara-falls') return import('./components/CityPages/NiagaraFallsPage');
  if (p === '/laval') return import('./components/CityPages/LavalPage');
  // City + service pages from the city-pages CMS: just that page's chunk
  const full = (window.location.pathname || '/').replace(/(.)\/+$/, '$1');
  const cmsKey = Object.keys(CMS_PAGES).find((k) => CMS_PAGES[k].en === full || CMS_PAGES[k].fr === full);
  if (cmsKey && CMS_CITY_SERVICE_LOADERS[cmsKey]) return CMS_CITY_SERVICE_LOADERS[cmsKey]();
  // The other city + service pages: the template and its city's content file
  if (/^\/(toronto|montreal|ottawa|quebec-city|quebec|trois-rivieres|niagara-falls|laval)\//.test(p)) {
    return Promise.all([import('./components/CityServicePage'), loadEntriesForPath(full)]);
  }
  return null;
};

// Detect whether the page was server/static-prerendered. If the #root
// container already has children (our Puppeteer prerender always fills it),
// we MUST use hydrateRoot — createRoot would discard the prerendered DOM
// and re-render from scratch, producing a massive layout shift (CLS ≈ 0.3+).
//
// We still preload the route's lazy chunks first so React.lazy components
// resolve synchronously on hydration and Suspense never has to swap a
// fallback in for the prerendered subtree (another CLS source).
const isPrerendered = container && container.firstChild != null && container.getAttribute('data-fallback') !== 'true';

let preloadPromise = null;
try {
  preloadPromise = preloadCurrentRoute();
} catch (_) {
  preloadPromise = null;
}

const mount = () => {
  if (isPrerendered) {
    ReactDOM.hydrateRoot(container, app);
  } else {
    ReactDOM.createRoot(container).render(app);
  }
};

// Cap each wait so a slow network never blocks render indefinitely. The page's
// language (French is a separate chunk) gets longer: hydrating the French HTML
// with English strings would make React re-render the whole page.
const capped = (promise, ms) =>
  Promise.race([promise.catch(() => {}), new Promise((resolve) => setTimeout(resolve, ms))]);

const ready = [capped(i18nReady, 8000)];
if (preloadPromise && typeof preloadPromise.then === 'function') ready.push(capped(preloadPromise, 1500));
Promise.all(ready).then(mount);
